/**
 * German public holidays per Bundesland (PRD §6.3) — re-derived from the reference's
 * `src/lib/holidays.ts`. Computed only; no table. Shared by client and server so the calendar
 * and the week-completion predicate cannot disagree about which days are free.
 */

import {
  addDays,
  formatBusinessDate,
  isoWeekday,
  isoWeekThursday,
  type BusinessDate,
} from "./dates";
import { BUNDESLAND_CODES, type Bundesland } from "./schemas/tenancy";

export type { Bundesland };

export const BUNDESLAND_LABELS: Record<Bundesland, string> = {
  BW: "Baden-Württemberg",
  BY: "Bayern",
  BE: "Berlin",
  BB: "Brandenburg",
  HB: "Bremen",
  HH: "Hamburg",
  HE: "Hessen",
  MV: "Mecklenburg-Vorpommern",
  NI: "Niedersachsen",
  NW: "Nordrhein-Westfalen",
  RP: "Rheinland-Pfalz",
  SL: "Saarland",
  SN: "Sachsen",
  ST: "Sachsen-Anhalt",
  SH: "Schleswig-Holstein",
  TH: "Thüringen",
};

/** Accepts a 2-letter code, NRW, or a German name; returns the canonical code or null. */
export function normalizeBundeslandCode(value: string | null | undefined): Bundesland | null {
  if (!value) return null;
  const upper = value.toString().trim().toUpperCase();
  if ((BUNDESLAND_CODES as readonly string[]).includes(upper)) return upper as Bundesland;

  const aliases: Record<string, Bundesland> = {
    NRW: "NW",
    "BADEN-WÜRTTEMBERG": "BW",
    "BADEN-WUERTTEMBERG": "BW",
    BAYERN: "BY",
    BAVARIA: "BY",
    BERLIN: "BE",
    BRANDENBURG: "BB",
    BREMEN: "HB",
    HAMBURG: "HH",
    HESSEN: "HE",
    HESSE: "HE",
    "MECKLENBURG-VORPOMMERN": "MV",
    NIEDERSACHSEN: "NI",
    "LOWER SAXONY": "NI",
    "NORDRHEIN-WESTFALEN": "NW",
    "NORTH RHINE-WESTPHALIA": "NW",
    "RHEINLAND-PFALZ": "RP",
    SAARLAND: "SL",
    SACHSEN: "SN",
    SAXONY: "SN",
    "SACHSEN-ANHALT": "ST",
    "SCHLESWIG-HOLSTEIN": "SH",
    THÜRINGEN: "TH",
    THUERINGEN: "TH",
    THURINGIA: "TH",
  };
  return aliases[upper] ?? null;
}

/** Gauss/Butcher — Easter Sunday (Gregorian) as a UTC midnight Date. */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function shiftEaster(year: number, offset: number): BusinessDate {
  const base = easterSunday(year);
  base.setUTCDate(base.getUTCDate() + offset);
  return formatBusinessDate(base);
}

/** Buß- und Bettag: Wednesday before 23 November (SN only). */
function bussUndBettag(year: number): BusinessDate {
  const day = new Date(Date.UTC(year, 10, 22));
  while (day.getUTCDay() !== 3) day.setUTCDate(day.getUTCDate() - 1);
  return formatBusinessDate(day);
}

/**
 * ISO date → holiday name for the year in the given Bundesland. Without a code, only the
 * nationwide set is returned.
 */
export function holidaysForYear(
  year: number,
  bundesland: string | null | undefined,
): Map<BusinessDate, string> {
  const map = new Map<BusinessDate, string>();
  const put = (date: BusinessDate, name: string) => map.set(date, name);

  put(`${year}-01-01` as BusinessDate, "Neujahr");
  put(shiftEaster(year, -2), "Karfreitag");
  put(shiftEaster(year, 1), "Ostermontag");
  put(`${year}-05-01` as BusinessDate, "Tag der Arbeit");
  put(shiftEaster(year, 39), "Christi Himmelfahrt");
  put(shiftEaster(year, 50), "Pfingstmontag");
  put(`${year}-10-03` as BusinessDate, "Tag der Deutschen Einheit");
  put(`${year}-12-25` as BusinessDate, "1. Weihnachtstag");
  put(`${year}-12-26` as BusinessDate, "2. Weihnachtstag");

  const code = normalizeBundeslandCode(bundesland);
  if (!code) return map;

  if (code === "BW" || code === "BY" || code === "ST") {
    put(`${year}-01-06` as BusinessDate, "Heilige Drei Könige");
  }
  if (code === "BE" && year >= 2019)
    put(`${year}-03-08` as BusinessDate, "Internationaler Frauentag");
  if (code === "BE" && year === 2025) put(`${year}-05-08` as BusinessDate, "Tag der Befreiung");
  if (code === "MV" && year >= 2023)
    put(`${year}-03-08` as BusinessDate, "Internationaler Frauentag");
  if (["BW", "BY", "HE", "NW", "RP", "SL"].includes(code)) {
    put(shiftEaster(year, 60), "Fronleichnam");
  }
  if (code === "BY" || code === "SL") put(`${year}-08-15` as BusinessDate, "Mariä Himmelfahrt");
  if (code === "TH" && year >= 2019) put(`${year}-09-20` as BusinessDate, "Weltkindertag");
  if (["BB", "MV", "SN", "ST", "TH", "HH", "NI", "HB", "SH"].includes(code)) {
    put(`${year}-10-31` as BusinessDate, "Reformationstag");
  }
  if (["BW", "BY", "NW", "RP", "SL"].includes(code)) {
    put(`${year}-11-01` as BusinessDate, "Allerheiligen");
  }
  if (code === "SN") put(bussUndBettag(year), "Buß- und Bettag");

  return map;
}

export function holidayName(
  date: BusinessDate,
  bundesland: string | null | undefined,
): string | null {
  const year = Number(date.slice(0, 4));
  return holidaysForYear(year, bundesland).get(date) ?? null;
}

export function isHoliday(date: BusinessDate, bundesland: string | null | undefined): boolean {
  return holidayName(date, bundesland) !== null;
}

/** Every calendar day of an ISO week (Mon–Sun), for week-save and completion. */
export function daysOfIsoWeek(year: number, isoWeek: number): BusinessDate[] {
  const thursday = isoWeekThursday(year, isoWeek);
  const monday = addDays(thursday, -3);
  return Array.from({ length: 7 }, (_unused, offset) => addDays(monday, offset));
}

/**
 * A week counts as saved when at least `workdaysPerWeek` Mon–Fri non-holiday days have an entry.
 * Profile `work_weekdays` only drive prefill; any Mon–Fri day may be tracked (flexible schedule).
 *
 * Optional filters (all off by default — existing callers stay byte-identical):
 * - `restrictToMonth` — only days in that calendar month (Live month cells / calendar parity)
 * - `windowFrom` / `windowTo` — assignment tracking window
 * - `notAfter` — exclude future days (Berlin "today" from the shared date utility)
 */
export function weekCompletion(input: {
  year: number;
  isoWeek: number;
  workdaysPerWeek: number;
  bundesland: string | null | undefined;
  savedDates: Iterable<BusinessDate>;
  restrictToMonth?: { year: number; month: number };
  windowFrom?: BusinessDate;
  windowTo?: BusinessDate | null;
  notAfter?: BusinessDate;
}): { saved: number; total: number; complete: boolean } {
  const saved = new Set(input.savedDates);
  const days = daysOfIsoWeek(input.year, input.isoWeek);
  const candidates: BusinessDate[] = [];

  for (const date of days) {
    const weekday = isoWeekday(date);
    if (weekday < 1 || weekday > 5) continue;
    if (isHoliday(date, input.bundesland)) continue;
    if (input.restrictToMonth) {
      const y = Number(date.slice(0, 4));
      const m = Number(date.slice(5, 7));
      if (y !== input.restrictToMonth.year || m !== input.restrictToMonth.month) continue;
    }
    if (input.windowFrom !== undefined && date < input.windowFrom) continue;
    if (input.windowTo != null && date > input.windowTo) continue;
    if (input.notAfter !== undefined && date > input.notAfter) continue;
    candidates.push(date);
  }

  const required = Math.min(Math.max(0, Math.trunc(input.workdaysPerWeek)), candidates.length);
  let counted = 0;
  for (const date of candidates) {
    if (saved.has(date)) counted += 1;
  }

  return {
    saved: Math.min(counted, required),
    total: required,
    complete: required > 0 && counted >= required,
  };
}

/** Workdays in a calendar month that are not holidays — used by the Live matrix. */
export function workdaysInMonth(
  year: number,
  month: number,
  workWeekdays: number[],
  bundesland: string | null | undefined,
): number {
  const workdays = new Set(workWeekdays);
  let count = 0;
  // Walk from the 1st; stop when the month rolls over.
  let date = `${year}-${String(month).padStart(2, "0")}-01` as BusinessDate;
  while (Number(date.slice(5, 7)) === month) {
    if (workdays.has(isoWeekday(date)) && !isHoliday(date, bundesland)) count += 1;
    date = addDays(date, 1);
  }
  return count;
}
